import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ShieldCheck } from "lucide-react";
import PublicNav from "../layout/PublicNav";
import PublicFooter from "../layout/PublicFooter";

/**
 * Shared frame for /workforce-os/* pages.
 *
 * - Reuses TRITAL Care PublicNav + PublicFooter (no redesign).
 * - Renders a small breadcrumb + page header.
 * - Adds the standard compliance disclaimer footer block at the bottom of children.
 */
export default function WorkforceOsLayout({
  eyebrow = "TRITAL Workforce OS™",
  title,
  description,
  children,
  showAiDisclaimer = false,
  breadcrumb,
  testId = "workforce-os-layout",
}) {
  return (
    <div data-testid={testId} className="min-h-screen flex flex-col bg-white">
      <PublicNav />

      {/* Page header */}
      <header className="border-b border-slate-200 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-10 lg:py-14">
          <nav
            className="text-[12px] text-slate-500 mb-3 flex items-center gap-1.5 flex-wrap"
            aria-label="Breadcrumb"
            data-testid="wos-breadcrumb"
          >
            <Link to="/" className="hover:text-trital-navy">Home</Link>
            <ChevronRight className="w-3 h-3" strokeWidth={1.8} />
            <Link to="/workforce-os" className="hover:text-trital-navy">Workforce OS</Link>
            {breadcrumb && (
              <>
                <ChevronRight className="w-3 h-3" strokeWidth={1.8} />
                <span className="text-slate-700">{breadcrumb}</span>
              </>
            )}
          </nav>
          <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.22em] font-bold text-trital-red">
            <ShieldCheck className="w-3 h-3" strokeWidth={2} /> {eyebrow}
          </div>
          {title && (
            <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-trital-navy leading-[1.04]">
              {title}
            </h1>
          )}
          {description && (
            <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-[64ch] leading-relaxed">
              {description}
            </p>
          )}
        </div>
      </header>

      <main className="flex-1">{children}</main>

      {/* Compliance disclaimer */}
      <section className="border-t border-slate-200 bg-slate-50" data-testid="wos-disclaimer">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-10">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
            <div className="overline text-slate-500">Compliance &amp; Human Review</div>
            <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">
              TRITAL Workforce OS™ supports recruiter productivity, candidate readiness, and
              staffing workflow intelligence. It does not replace human recruiter review,
              credentialing review, client approval, payroll verification, legal review, or
              clinical hiring decisions.
            </p>
            {showAiDisclaimer && (
              <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed">
                AI responses are informational and must be reviewed by a TRITAL Care recruiter
                before submission, hiring, pay-package confirmation, or compliance decision.
              </p>
            )}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
